import React, {Dispatch, SetStateAction} from 'react';
import {Root as DialogRoot} from '@radix-ui/react-dialog';
import Image from 'next/image';

import {PaymentMethodType} from '#apollo/schema';
import {Button, Link, Modal, Text} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import {text} from '#theme/text';

import * as css from './EditPaymentConfirmationModal.css';

interface EditPaymentConfirmationModalProps {
  paymentMethodType: PaymentMethodType;
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

export function EditPaymentConfirmationModal({
  paymentMethodType,
  open,
  onOpenChange,
}: EditPaymentConfirmationModalProps) {
  // Display different content for Shop Pay
  if (paymentMethodType === PaymentMethodType.Shoppay) {
    const helpCenterUrl = `${
      process.env.NEXT_PUBLIC_HELP_CENTER_URL || ''
    }/how-to-update-payment-method-in-shop-app-2238782`;

    return (
      <DialogRoot open={open} onOpenChange={onOpenChange}>
        <Modal layout="drawer" style={{width: '37.5rem'}}>
          <div className={css.root}>
            <div className={css.header}>
              <Text variant="bodyXLarge" color="burgundy" className={css.text}>
                Email sent
              </Text>
              <Button icon="X" as={ModalClose} variant="secondary" />
            </div>
            <Text
              as="div"
              variant="body"
              color="doveGray"
              style={text.body.medium as unknown as React.CSSProperties}>
              <p>
                We’ve sent you an email with a secure link to the page where you can update your
                payment details. If you don’t see it in your inbox, please check your spam folder or
                reach out to us at{' '}
                <Link href="mailto:hello@tallyhealth.com">hello@tallyhealth.com</Link>.
              </p>
            </Text>
            <div className={css.contentModal}>
              <Text variant="bodyLarge" color="burgundy" className={css.text}>
                What’s Next?
              </Text>
              <Text
                as="div"
                variant="caption"
                className={css.secondaryItemText}
                color="doveGray"
                style={text.body.medium as unknown as React.CSSProperties}>
                <p>
                  1.{' '}
                  <Link href={helpCenterUrl} target="_blank">
                    Click here
                  </Link>{' '}
                  for instructions on how to update your credit card in the Shop app.
                </p>
              </Text>
              <Text
                as="div"
                variant="caption"
                color="doveGray"
                style={text.body.medium as unknown as React.CSSProperties}>
                <p>
                  2. Open the email and click the{' '}
                  <Text
                    variant="caption"
                    fontWeight="semiBold"
                    style={text.body.medium as unknown as React.CSSProperties}>
                    Update Payment Method{' '}
                  </Text>
                  button and follow the instructions linked above.
                </p>
              </Text>
              <Text
                as="div"
                variant="caption"
                className={css.secondaryItemText}
                color="doveGray"
                fontWeight="semiBold"
                style={{
                  ...(text.body.small as unknown as React.CSSProperties),
                  paddingTop: '1rem',
                }}>
                <p>
                  Don’t want to use the Shop app for your Tally subscription anymore? Email our
                  Customer Success team at{' '}
                  <Link href="mailto:hello@tallyhealth.com">hello@tallyhealth.com</Link> and we’ll
                  be happy to help you switch.
                </p>
              </Text>
            </div>
          </div>
        </Modal>
      </DialogRoot>
    );
  }

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <Modal layout="drawer" style={{width: '37.5rem'}}>
        <div className={css.root}>
          <div className={css.header}>
            <Text variant="bodyXLarge" color="burgundy" className={css.text}>
              Email sent
            </Text>
            <Button icon="X" as={ModalClose} variant="secondary" />
          </div>
          <Text
            as="div"
            variant="body"
            color="doveGray"
            style={text.body.medium as unknown as React.CSSProperties}>
            <p>
              We’ve sent you an email with a secure link to the page where you can update your
              payment details. If you don’t see it in your inbox, please check your spam folder or
              reach out to us at{' '}
              <Link href="mailto:hello@tallyhealth.com">hello@tallyhealth.com</Link>.
            </p>
          </Text>
          <div className={css.contentModal}>
            <Text variant="bodyLarge" color="burgundy" className={css.text}>
              What’s Next?
            </Text>
            <Text
              as="div"
              variant="caption"
              color="doveGray"
              style={text.body.medium as unknown as React.CSSProperties}>
              <p>
                1. Open the email and click the{' '}
                <Text
                  variant="caption"
                  fontWeight="semiBold"
                  style={text.body.medium as unknown as React.CSSProperties}>
                  Update Payment Method{' '}
                </Text>
                button.
              </p>
            </Text>
            <div style={css.imageWrapper}>
              <Image
                alt="Payment Screen Print"
                width={226}
                height={323}
                src="/image/profile/payment/payment-print.png"
              />
            </div>
            <Text
              as="div"
              variant="caption"
              className={css.secondaryItemText}
              color="doveGray"
              style={text.body.medium as unknown as React.CSSProperties}>
              <p>
                2. Update your payment information using the form provided in the link and when
                you’re done click{' '}
                <Text
                  variant="caption"
                  fontWeight="semiBold"
                  style={text.body.medium as unknown as React.CSSProperties}>
                  Update card.
                </Text>
              </p>
            </Text>
          </div>
        </div>
      </Modal>
    </DialogRoot>
  );
}
